import * as Sentry from "@sentry/vue";
import { i18n } from "@verbleif/lib/src/translations/i18n";
import { isAxiosError } from "axios";
import { useI18n } from "vue-i18n";
import { toast } from "vue-sonner";
import AbortedError from "../Api/Error/AbortedError";

interface ServerErrorTranslator {
  t: (key: string, params?: Record<string, unknown>) => string
  te: (key: string) => boolean
}

/**
 * Prefer `useI18n()` inside setup. Auth catalog refresh and other async catch blocks can run outside
 * setup, where `useI18n()` throws I18nErrorCodes 26, so fall back to the plugin global composer.
 */
function resolveTranslator(): ServerErrorTranslator {
  try {
    const composer = useI18n();
    return {
      t: (key, params) => String(composer.t(key, params ?? {})),
      te: key => composer.te(key),
    };
  } catch {
    return {
      t: (key, params) => String(i18n.global.t(key, params ?? {})),
      te: key => i18n.global.te(key),
    };
  }
}

function isAbortError(error: unknown): boolean {
  if (error instanceof AbortedError) {
    return true;
  }

  return Boolean(
    error
    && typeof error === "object"
    && "name" in error
    && (error as { name?: string }).name === "AbortedError",
  );
}

export function useServerErrorHandler() {
  function onError({
    error,
    violationsPath = "violations.",
    hideToast = false,
    toastTitle,
    toastId,
  }: {
    error: any
    violationsPath?: string
    hideToast?: boolean
    toastTitle?: string
    toastId?: string | number
  }) {
    const { te, t } = resolveTranslator();
    let detail = t("base.unknown_error");
    if (
      isAxiosError(error)
      && error.response?.data?.detail
    ) {
      detail = error.response.data.detail;
    }

    if (
      isAxiosError(error)
      && error.response?.status === 403
      && error.response?.data?.detail === "user_shared_outside_scope"
    ) {
      detail = t("access.grants.shared_outside_scope");
    }

    if (
      isAxiosError(error)
      && typeof error.response?.data?.detail === "string"
      && error.response.data.detail.includes("LEGACY_WRITES_FROZEN")
    ) {
      detail = t("access.grants.legacy_frozen");
    }

    if (
      isAxiosError(error)
      && error.response?.data?.violations
    ) {
      let violationsDetails = "";
      for (const violation of error.response.data.violations) {
        const numbers: number[] = [];
        const output = violation.propertyPath.replace(/\[(\d+)\]/g, (_: any, p1: string) => {
          numbers.push(Number(p1));
          return "";
        });
        const firstNumber = numbers ? numbers[0] ?? 0 : 0;
        const propertyPathSplided = output.split(".");
        let propertyPath = "";
        let lastKey = "";
        propertyPathSplided.forEach((path: string, index: number) => {
          const key = t(`${violationsPath}.${lastKey + path}._label`, { count: firstNumber });
          const computedKey = key.includes("_label") ? path : key;
          propertyPath += `${computedKey}${index === propertyPathSplided.length - 1 ? "" : "->"}`;
          lastKey = `${lastKey + path}.`;
        });
        violationsDetails += `[${propertyPath}]: ${violation.message}\n`;
      }
      detail = violationsDetails;
    }

    if (
      detail === t("base.unknown_error")
      && isAxiosError(error)
      && error.response?.status
    ) {
      const translationsExists = te(`base.${error.response.status}`);
      detail = translationsExists ? t(`base.${error.response.status}`) : t("base.unknown_code", { errorCode: error.response.status });
    }

    const title = toastTitle ?? detail;
    const description = toastTitle ? detail : undefined;
    console.error("Error occurred:", error, { title, description });

    // An AbortedError is the session ending on a refused refresh (oauth.invalid_grant): the
    // user is being logged out, so there is nothing to show and nothing to report.
    if (isAbortError(error)) {
      return detail;
    }

    if (!hideToast) {
      if ((isAxiosError(error) && error.response?.status === 403) && error.response?.data?.detail?.includes(
        "Switch User failed: User",
      )) {
        return detail;
      }

      // Updating an Infinity toast by id does not apply a new duration (sonner).
      if (toastId !== undefined) {
        toast.dismiss(toastId);
      }
      toast.error(title, {
        description,
        duration: 3000,
      });
    }

    // 422 is a validation answer the form already shows.
    if (isAxiosError(error) && error.response?.status === 422) {
      return detail;
    }

    if (Sentry.isInitialized()) {
      Sentry.captureException(error);
    }

    return detail;
  }
  return {
    onError,
  };
}
